/**
 * Multi-School AI Summary Tab
 *
 * Admin-only tool to generate on-demand "rollup" AI summaries across a set of
 * 2+ schools (e.g. every location belonging to a multi-site operator). Each
 * rollup is synthesized from the schools' already-generated single-school AI
 * summaries (not raw data) — geographic/competitive/projections summaries are
 * each rolled up, then an overview synthesizes those three rollups, mirroring
 * how the single-school "combined" summary synthesizes geographic/
 * competitive/projections for one school.
 *
 * Results are persisted server-side keyed by the selected school set, so
 * re-selecting the same schools reuses prior generations instead of
 * re-calling the AI.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider
} from '@mui/material';
import {
  Summarize as SummarizeIcon,
  Public as PublicIcon,
  DonutSmall as CompetitiveIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  PlaylistAddCheck as SelectAllIcon,
  ClearAll as ClearAllIcon,
  Groups as GroupsIcon,
  History as HistoryIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminService } from '../../../../services/adminService';
import {
  generateMultiSchoolSummary,
  getMultiSchoolCoverage,
  getStoredMultiSchoolSummaries,
  getMultiSchoolHistory
} from '../../../../services/aiService';
import FormattedNarrative from '../FormattedNarrative';
import MultiSchoolCoverageTable from './MultiSchoolCoverageTable';
import PromptEditor, { MULTI_SCHOOL_PROMPT_TYPES } from '../PromptEditor';

const DRIVE_TIMES = [5, 10, 15];
const MIN_SCHOOLS = 2;
const MAX_SCHOOLS = 25;

const RESULT_TABS = [
  { key: 'overview', label: 'Overview', icon: <SummarizeIcon fontSize="small" /> },
  { key: 'geographic', label: 'Geographic', icon: <PublicIcon fontSize="small" /> },
  { key: 'competitive', label: 'Competitive', icon: <CompetitiveIcon fontSize="small" /> },
  { key: 'projections', label: 'Projections', icon: <TrendingUpIcon fontSize="small" /> }
];

const EMPTY_RESULTS = { overview: null, geographic: null, competitive: null, projections: null };

const normalizeStoredResult = (row) => {
  if (!row) return null;
  return {
    ...row,
    narrative: row.narrative || row.full_narrative || ''
  };
};

const schoolsFromHistory = (item, teamSchools = []) => {
  const byUuid = new Map(teamSchools.map((s) => [s.uuid, s]));
  return (item.school_uuids || []).map((uuid, i) => (
    byUuid.get(uuid) || { uuid, name: (item.school_names && item.school_names[i]) || uuid }
  ));
};

const MultiSchoolSummaryTab = () => {
  const { enqueueSnackbar } = useSnackbar();

  // School selection
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamSchools, setTeamSchools] = useState([]);
  const [loadingTeamSchools, setLoadingTeamSchools] = useState(false);
  const [selectedSchools, setSelectedSchools] = useState([]);

  // Parameters
  const [driveTime, setDriveTime] = useState(10);
  const [travelMode, setTravelMode] = useState('drive');
  const [sourceDetail, setSourceDetail] = useState('executive');

  // Coverage + stored results
  const [coverage, setCoverage] = useState({});
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [loadingStored, setLoadingStored] = useState(false);
  const [results, setResults] = useState(EMPTY_RESULTS);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatingType, setGeneratingType] = useState(null);

  // Results view
  const [activeResultTab, setActiveResultTab] = useState(0);

  // Previously generated rollups (survives logout; loaded from the DB)
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sectionTab, setSectionTab] = useState(0);

  const pendingRestoreRef = useRef(null);

  const selectedUuids = useMemo(() => selectedSchools.map(s => s.uuid), [selectedSchools]);
  const selectedUuidsKey = useMemo(() => [...selectedUuids].sort().join(','), [selectedUuids]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const resp = await getMultiSchoolHistory(20);
      setHistory(resp.history || []);
    } catch (err) {
      console.error('Error loading multi-school history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Load all teams on mount (same pattern as the single-school Generate & View tab)
  useEffect(() => {
    const loadAllTeams = async () => {
      setLoadingTeams(true);
      try {
        const response = await adminService.listTeams(0, 1000, enqueueSnackbar, true);
        const teamsData = response?.teams || [];
        setTeams(teamsData.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (err) {
        console.error('Error loading teams:', err);
        enqueueSnackbar('Failed to load teams', { variant: 'error' });
      } finally {
        setLoadingTeams(false);
      }
    };
    loadAllTeams();
    loadHistory();
  }, [enqueueSnackbar, loadHistory]);

  // Load schools for the selected team
  useEffect(() => {
    if (!selectedTeam) {
      setTeamSchools([]);
      return;
    }

    const loadSchoolsForTeam = async () => {
      setLoadingTeamSchools(true);
      try {
        const response = await adminService.getTeamSchools(selectedTeam.id, enqueueSnackbar);
        const schools = (response?.schools || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setTeamSchools(schools);
        const pending = pendingRestoreRef.current;
        if (pending) {
          setSelectedSchools(schoolsFromHistory(pending, schools));
          pendingRestoreRef.current = null;
        } else {
          setSelectedSchools([]);
        }
      } catch (err) {
        console.error('Error loading team schools:', err);
        setTeamSchools([]);
        setSelectedSchools([]);
      } finally {
        setLoadingTeamSchools(false);
      }
    };

    loadSchoolsForTeam();
  }, [selectedTeam, enqueueSnackbar]);

  // Load coverage + any already-stored rollups whenever the selected school set,
  // drive time, or travel mode changes.
  useEffect(() => {
    if (selectedUuids.length < MIN_SCHOOLS) {
      setCoverage({});
      setResults(EMPTY_RESULTS);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoadingCoverage(true);
      try {
        const covResp = await getMultiSchoolCoverage(selectedUuids, driveTime, travelMode);
        if (!cancelled) setCoverage(covResp.coverage || {});
      } catch (err) {
        console.error('Error loading multi-school coverage:', err);
        if (!cancelled) enqueueSnackbar('Failed to load source coverage', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingCoverage(false);
      }

      setLoadingStored(true);
      try {
        const storedResp = await getStoredMultiSchoolSummaries(selectedUuids, driveTime, travelMode);
        const stored = storedResp.summaries || {};
        if (!cancelled) {
          setResults({
            overview: normalizeStoredResult(stored.overview),
            geographic: normalizeStoredResult(stored.geographic),
            competitive: normalizeStoredResult(stored.competitive),
            projections: normalizeStoredResult(stored.projections)
          });
        }
      } catch (err) {
        console.error('Error loading stored multi-school summaries:', err);
        if (!cancelled) setResults(EMPTY_RESULTS);
      } finally {
        if (!cancelled) setLoadingStored(false);
      }
    })();

    return () => { cancelled = true; };
    // selectedUuidsKey captures the set identity; selectedUuids itself is a new
    // array each render so it's intentionally excluded here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUuidsKey, driveTime, travelMode, enqueueSnackbar]);

  const handleSelectAll = () => setSelectedSchools(teamSchools.slice(0, MAX_SCHOOLS));
  const handleClearSelection = () => setSelectedSchools([]);

  const handleOpenHistory = (item) => {
    setDriveTime(item.drive_time);
    setTravelMode(item.travel_mode || 'drive');
    const team = teams.find((t) => t.id === item.team_id) || null;
    if (team && selectedTeam?.id === team.id) {
      setSelectedSchools(schoolsFromHistory(item, teamSchools));
      return;
    }
    if (team) {
      pendingRestoreRef.current = item;
      setSelectedTeam(team);
      return;
    }
    setSelectedTeam(null);
    setSelectedSchools(schoolsFromHistory(item));
  };

  const generateOne = useCallback(async (type, priorSummaries = null) => {
    setGeneratingType(type);
    try {
      const result = await generateMultiSchoolSummary(selectedUuids, type, driveTime, travelMode, {
        sourceDetail,
        priorSummaries,
        teamId: selectedTeam?.id
      });
      setResults(prev => ({ ...prev, [type]: result }));
      loadHistory();
      if (result.skipped_schools?.length) {
        enqueueSnackbar(
          `${type} rollup generated, but skipped ${result.skipped_schools.length} school(s) missing that summary`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(`Generated ${type} rollup`, { variant: 'success' });
      }
      return result;
    } catch (err) {
      console.error(`Error generating multi-school ${type} summary:`, err);
      const skipped = err?.data?.skipped_schools;
      enqueueSnackbar(err.message || `Failed to generate ${type} rollup`, { variant: 'error' });
      const errorResult = { error: err.message, skipped_schools: skipped };
      setResults(prev => ({ ...prev, [type]: errorResult }));
      return errorResult;
    } finally {
      setGeneratingType(null);
    }
  }, [selectedUuids, driveTime, travelMode, sourceDetail, selectedTeam, enqueueSnackbar, loadHistory]);

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const geo = await generateOne('geographic');
      const comp = await generateOne('competitive');
      const proj = await generateOne('projections');
      await generateOne('overview', { geographic: geo, competitive: comp, projections: proj });
      setActiveResultTab(0);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (type) => {
    if (type === 'overview') {
      await generateOne('overview', {
        geographic: results.geographic,
        competitive: results.competitive,
        projections: results.projections
      });
    } else {
      await generateOne(type);
    }
  };

  const schoolCount = selectedUuids.length;
  const tooFew = schoolCount > 0 && schoolCount < MIN_SCHOOLS;
  const tooMany = schoolCount > MAX_SCHOOLS;
  const canGenerate = schoolCount >= MIN_SCHOOLS && schoolCount <= MAX_SCHOOLS && !generating && !generatingType;
  const isBusy = generating || !!generatingType;

  const activeKey = RESULT_TABS[activeResultTab].key;
  const activeResult = results[activeKey];

  return (
    <Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={sectionTab}
          onChange={(e, newValue) => setSectionTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Generate" icon={<GroupsIcon />} iconPosition="start" />
          <Tab label="Prompts" icon={<EditIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {sectionTab === 1 && (
        <PromptEditor variant="inline" types={MULTI_SCHOOL_PROMPT_TYPES} />
      )}

      {sectionTab === 0 && (
      <Box>
      <Alert severity="info" icon={<GroupsIcon />} sx={{ mb: 2 }}>
        Generate a portfolio-level rollup across 2 or more schools by feeding the AI what's already been
        generated for each school individually (their stored geographic, competitive, and projections
        summaries) — not raw data. Useful for operators with multiple locations who want a single
        overview across their portfolio. Saved rollups are listed below and can be reopened after logout.
        Generating for a selected team publishes that rollup to users on that team at /ai-summary.
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon /> Recent Rollups
        </Typography>
        {loadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : history.length === 0 ? (
          <Typography color="text.secondary">
            No saved multi-school rollups yet. Generate one below and it will appear here.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {history.map((item) => {
              const teamName = teams.find((t) => t.id === item.team_id)?.name;
              const isCurrent = item.school_uuids?.slice().sort().join(',') === selectedUuidsKey
                && item.drive_time === driveTime
                && (item.travel_mode || 'drive') === travelMode;
              return (
                <Box
                  key={`${item.school_set_hash}-${item.drive_time}-${item.travel_mode}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    backgroundColor: isCurrent ? 'action.selected' : 'transparent'
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="subtitle2">
                      {teamName || 'Saved school set'} · {item.school_count} schools
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.drive_time}-minute {item.travel_mode || 'drive'}
                      {item.last_generated ? ` · ${new Date(item.last_generated).toLocaleString()}` : ''}
                    </Typography>
                  </Box>
                  {item.team_id && (
                    <Chip size="small" color="success" variant="outlined" label="Published to users" />
                  )}
                  {(item.types_present || []).map((t) => (
                    <Chip key={t} size="small" label={t} variant="outlined" />
                  ))}
                  <Button size="small" variant={isCurrent ? 'contained' : 'outlined'} onClick={() => handleOpenHistory(item)}>
                    {isCurrent ? 'Loaded' : 'Open'}
                  </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* School Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupsIcon /> School Selection
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a team before generating. Users on that team will see this rollup as a Portfolio option on /ai-summary.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={selectedTeam}
              onChange={(event, newValue) => setSelectedTeam(newValue)}
              options={teams}
              getOptionLabel={(option) => option.name || ''}
              loading={loadingTeams}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Team"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTeams ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              multiple
              value={selectedSchools}
              onChange={(event, newValue) => setSelectedSchools(newValue.slice(0, MAX_SCHOOLS))}
              options={teamSchools}
              filterSelectedOptions
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              loading={loadingTeamSchools}
              disabled={!selectedTeam}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Schools (2+)"
                  variant="outlined"
                  placeholder={selectedSchools.length ? '' : 'Select 2 or more schools'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTeamSchools ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                startIcon={<SelectAllIcon />}
                onClick={handleSelectAll}
                disabled={!selectedTeam || teamSchools.length === 0}
              >
                Select All ({teamSchools.length})
              </Button>
              <Button
                size="small"
                startIcon={<ClearAllIcon />}
                onClick={handleClearSelection}
                disabled={selectedSchools.length === 0}
              >
                Clear
              </Button>
              <Box sx={{ flex: 1 }} />
              <Chip
                size="small"
                color={tooFew || tooMany ? 'warning' : 'default'}
                label={`${schoolCount} school${schoolCount === 1 ? '' : 's'} selected`}
              />
            </Box>
          </Grid>
        </Grid>
        {tooFew && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Select at least {MIN_SCHOOLS} schools to generate a multi-school rollup.
          </Alert>
        )}
        {tooMany && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            A maximum of {MAX_SCHOOLS} schools is supported per rollup. Only the first {MAX_SCHOOLS} selected will be used.
          </Alert>
        )}
      </Paper>

      {schoolCount >= MIN_SCHOOLS && (
        <>
          {/* Parameters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Drive Time</InputLabel>
                  <Select value={driveTime} onChange={(e) => setDriveTime(e.target.value)} label="Drive Time">
                    {DRIVE_TIMES.map(time => (
                      <MenuItem key={time} value={time}>{time} minutes</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Travel Mode</InputLabel>
                  <Select value={travelMode} onChange={(e) => setTravelMode(e.target.value)} label="Travel Mode">
                    <MenuItem value="drive">Driving</MenuItem>
                    <MenuItem value="walk">Walking</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <ToggleButtonGroup
                  size="small"
                  value={sourceDetail}
                  exclusive
                  onChange={(e, val) => val && setSourceDetail(val)}
                >
                  <ToggleButton value="executive">Executive Summaries</ToggleButton>
                  <ToggleButton value="full">Full Narratives</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerateAll}
                    disabled={!canGenerate}
                    startIcon={isBusy ? <CircularProgress size={18} color="inherit" /> : <SummarizeIcon />}
                  >
                    {isBusy
                      ? (generatingType ? `Generating ${generatingType}...` : 'Generating...')
                      : 'Generate All'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              "Executive Summaries" feeds the AI a shorter, cheaper source per school. "Full Narratives" feeds
              the complete stored summary for more detail at higher token cost.
            </Typography>
          </Paper>

          {/* Coverage */}
          <MultiSchoolCoverageTable
            schools={selectedSchools}
            coverage={coverage}
            driveTime={driveTime}
            travelMode={travelMode}
            loading={loadingCoverage}
          />

          {/* Results */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeResultTab}
              onChange={(e, newValue) => setActiveResultTab(newValue)}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {RESULT_TABS.map((tab) => (
                <Tab key={tab.key} label={tab.label} icon={tab.icon} iconPosition="start" />
              ))}
            </Tabs>

            <Box sx={{ p: 3 }}>
              {loadingStored ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !activeResult ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" gutterBottom>
                    No {RESULT_TABS[activeResultTab].label.toLowerCase()} rollup generated yet for this school set.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={generatingType === activeKey ? <CircularProgress size={18} /> : <RefreshIcon />}
                    onClick={() => handleRegenerate(activeKey)}
                    disabled={!canGenerate && generatingType !== activeKey}
                    sx={{ mt: 1 }}
                  >
                    Generate {RESULT_TABS[activeResultTab].label}
                  </Button>
                </Box>
              ) : activeResult.error ? (
                <Alert severity="error">
                  {activeResult.error}
                  {activeResult.skipped_schools?.length > 0 && (
                    <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                      {activeResult.skipped_schools.map((s) => (
                        <li key={s.uuid}>{s.name}: {s.reason}</li>
                      ))}
                    </Box>
                  )}
                </Alert>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {activeResult.mode && (
                      <Chip
                        size="small"
                        label={activeResult.mode === 'mock' ? 'Mock (no API key)' : 'AI Generated'}
                        color={activeResult.mode === 'mock' ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    )}
                    {activeResult.generated_at && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Generated ${new Date(activeResult.generated_at).toLocaleString()}`}
                      />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      startIcon={generatingType === activeKey ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={() => handleRegenerate(activeKey)}
                      disabled={!canGenerate && generatingType !== activeKey}
                    >
                      Regenerate
                    </Button>
                  </Box>

                  {activeResult.skipped_schools?.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Skipped {activeResult.skipped_schools.length} school(s) missing this summary type:
                      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                        {activeResult.skipped_schools.map((s) => (
                          <li key={s.uuid}>{s.name}: {s.reason}</li>
                        ))}
                      </Box>
                    </Alert>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  <FormattedNarrative
                    narrative={activeResult.narrative}
                    parsedSections={activeResult.sections}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </>
      )}
    </Box>
      )}
    </Box>
  );
};

export default MultiSchoolSummaryTab;
